"use client";

import { CustomerSlugInput } from "../categories/customer-slug-input";
import { customerSlugError } from "@/lib/categories/attribute-slugs";
import {
  completeLanguageMap,
  localizedText,
  sparseLanguageMap,
} from "@/lib/categories/localization";

import { useState } from "react";
import {
  Loader2,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { deriveAttributeKey } from "@/lib/categories/attribute-key";
import { descendantCount } from "@/lib/categories/tree-model";
import { CategoryNavigator } from "@/components/catalog/categories/category-navigator";
import { CategoryRowMenu } from "@/components/catalog/categories/category-row-menu";
import { ConfirmDialog } from "@/components/catalog/categories/confirm-dialog";
import {
  CatalogueTemplateService,
  type CategoryTemplateCategory,
} from "@/services/catalogue-template.service";

interface CategoryTemplateTreeProps {
  templateId: string;
  categories: CategoryTemplateCategory[];
  languages: string[];
  readOnly: boolean;
  selectedCategoryId: string | null;
  onSelectCategory: (categoryId: string | null) => void;
  onChanged: () => void;
}

type CategoryNode = CategoryTemplateCategory & { children: CategoryNode[] };

/** The template tree has no unsaved-draft concept. */
const NO_DRAFTS: ReadonlySet<string> = new Set();

interface CategoryFormState {
  category_slug: Record<string, string>;
  key: string;
  name: Record<string, string>;
  description: string;
  sortOrder: string;
}

type CategoryDialogState =
  | { mode: "create"; parentId: string | null; parentName: string | null }
  | { mode: "edit"; category: CategoryTemplateCategory }
  | null;

const emptyForm = (languages: readonly string[]): CategoryFormState => ({
  category_slug: Object.fromEntries(languages.map((language) => [language, ""])),
  key: "",
  name: Object.fromEntries(languages.map((language) => [language, ""])),
  description: "",
  sortOrder: "0",
});

function buildTree(categories: CategoryTemplateCategory[]): CategoryNode[] {
  const sorted = [...categories].sort((a, b) => {
    if (a.sortOrder !== b.sortOrder) return a.sortOrder - b.sortOrder;
    return localizedText(a.name).localeCompare(localizedText(b.name));
  });
  const byId = new Map<string, CategoryNode>();
  for (const category of sorted) {
    byId.set(category.id, { ...category, children: [] });
  }

  const roots: CategoryNode[] = [];
  for (const node of byId.values()) {
    if (node.parentId && byId.has(node.parentId)) {
      byId.get(node.parentId)!.children.push(node);
    } else {
      roots.push(node);
    }
  }
  return roots;
}

function parseSortOrder(value: string): number {
  const parsed = Number.parseInt(value, 10);
  return Number.isFinite(parsed) ? parsed : 0;
}

export function CategoryTemplateTree({
  templateId,
  categories,
  languages,
  readOnly,
  selectedCategoryId,
  onSelectCategory,
  onChanged,
}: CategoryTemplateTreeProps) {
  const tree = buildTree(categories);
  const [dialog, setDialog] = useState<CategoryDialogState>(null);
  const [form, setForm] = useState<CategoryFormState>(() => emptyForm(languages));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<CategoryNode | null>(null);



  const updateForm = (patch: Partial<CategoryFormState>) => {
    setForm((current) => ({ ...current, ...patch }));
  };

  const openCreate = (parent: CategoryTemplateCategory | null) => {
    setForm(emptyForm(languages));
    setError(null);
    setDialog({
      mode: "create",
      parentId: parent?.id ?? null,
      parentName: parent ? localizedText(parent.name) : null,
    });
  };

  const openEdit = (category: CategoryTemplateCategory) => {
    setForm({
      category_slug: Object.fromEntries(
        languages.map((language) => [language, category.category_slug?.[language] ?? ""]),
      ),
      key: category.key,
      name: Object.fromEntries(
        languages.map((language) => [language, category.name[language] ?? ""]),
      ),
      description: category.description ?? "",
      sortOrder: String(category.sortOrder),
    });
    setError(null);
    setDialog({ mode: "edit", category });
  };

  const submitDialog = async () => {
    if (!dialog) return;

    const slugError = languages
      .map((language) => customerSlugError(form.category_slug[language] ?? ""))
      .find(Boolean);
    if (slugError) {
      setError(slugError);
      return;
    }
    const key = form.key.trim();
    if (!key || languages.some((language) => !form.name[language]?.trim())) {
      setError("Key and every language name are required");
      return;
    }

    setBusy(true);
    setError(null);
    const description = form.description.trim();
    const categorySlug = sparseLanguageMap(languages, form.category_slug);
    const payload = {
      category_slug: Object.keys(categorySlug).length ? categorySlug : null,
      key,
      name: completeLanguageMap(languages, form.name),
      description: description || undefined,
      sort_order: parseSortOrder(form.sortOrder),
    };

    try {
      if (dialog.mode === "create") {
        const created = await CatalogueTemplateService.createCategoryTemplateCategory(
          templateId,
          { ...payload, parent_id: dialog.parentId },
        );
        onSelectCategory(created.id);
      } else {
        await CatalogueTemplateService.updateCategoryTemplateCategory(
          templateId,
          dialog.category.id,
          { ...payload, description: description || null },
        );
      }
      setDialog(null);
      onChanged();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Category save failed");
    } finally {
      setBusy(false);
    }
  };

  const deleteCategory = async (category: CategoryTemplateCategory) => {
    setBusy(true);
    setError(null);
    try {
      await CatalogueTemplateService.deleteCategoryTemplateCategory(templateId, category.id);
      if (selectedCategoryId === category.id) onSelectCategory(null);
      setConfirmDelete(null);
      onChanged();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Category delete failed");
      setConfirmDelete(null);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex h-full min-h-0 flex-col">
      <CategoryNavigator
        nodes={tree}
        loading={false}
        error={null}
        selectedId={selectedCategoryId}
        draftIds={NO_DRAFTS}
        readOnly={readOnly}
        createLabel="Category"
        pinnedEntry={{
          label: "Default attributes",
          hint: "Used by any category in this template that has none of its own",
          selected: selectedCategoryId === null,
          onSelect: () => onSelectCategory(null),
        }}
        onSelect={(node) => onSelectCategory(node.id)}
        onCreateRoot={() => openCreate(null)}
        renderRowMenu={(node) => (
          <CategoryRowMenu
            node={node}
            readOnly={readOnly}
            showEnabled={false}
            onAdd={(parent) => openCreate(parent)}
            onRename={(target) => openEdit(target)}
            onExpandBranch={() => undefined}
            onToggleEnabled={() => undefined}
            onDelete={(target) => setConfirmDelete(target)}
          />
        )}
      />

      <ConfirmDialog
        open={!!confirmDelete}
        title={confirmDelete ? `Delete “${localizedText(confirmDelete.name)}”?` : ""}
        body={
          confirmDelete ? (
            <>
              {descendantCount(confirmDelete) > 0 && (
                <>
                  Its <strong>{descendantCount(confirmDelete)}</strong>{" "}
                  {descendantCount(confirmDelete) === 1
                    ? "subcategory"
                    : "subcategories"}{" "}
                  will be deleted too.{" "}
                </>
              )}
              Any attributes defined on{" "}
              {descendantCount(confirmDelete) > 0 ? "them" : "it"} go too. This only
              changes the draft template.
            </>
          ) : null
        }
        confirmLabel="Delete"
        destructive
        busy={busy}
        onConfirm={() => confirmDelete && void deleteCategory(confirmDelete)}
        onOpenChange={(open) => !open && setConfirmDelete(null)}
      />

      <Dialog open={!!dialog} onOpenChange={(open) => !open && setDialog(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {dialog?.mode === "create"
                ? dialog.parentName
                  ? `Add a subcategory under “${dialog.parentName}”`
                  : "Add a top-level category"
                : `Edit “${dialog?.category ? localizedText(dialog.category.name) : ""}”`}
            </DialogTitle>
            <DialogDescription>
              No two categories in this template can share an id.
            </DialogDescription>
          </DialogHeader>

          <div className="grid gap-4 py-2">
            <div className="grid gap-3 sm:grid-cols-2">
              {languages.map((language) => (
                <div key={language} className="grid gap-2">
                  <Label htmlFor={`template-category-name-${language}`}>
                    {language.toUpperCase()} name
                  </Label>
                  <Input
                    id={`template-category-name-${language}`}
                    autoFocus={language === "en"}
                    value={form.name[language] ?? ""}
                    aria-invalid={!form.name[language]?.trim()}
                    onChange={(event) => {
                      const name = { ...form.name, [language]: event.target.value };
                      updateForm(
                        dialog?.mode === "create" && language === "en"
                          ? {
                              name,
                              key: deriveAttributeKey(event.target.value),
                            }
                          : { name },
                      );
                    }}
                    placeholder="Required"
                  />
                </div>
              ))}
            </div>
            <div className="grid gap-2">
              <Label htmlFor="template-category-key">
                Id{" "}
                <span className="font-normal text-muted-foreground">
                  (used by the pipeline)
                </span>
              </Label>
              <Input
                id="template-category-key"
                className="font-mono text-[13px]"
                value={form.key}
                onChange={(event) => updateForm({ key: event.target.value })}
                placeholder="sofas"
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="template-category-description">Description</Label>
              <Textarea
                id="template-category-description"
                value={form.description}
                onChange={(event) => updateForm({ description: event.target.value })}
                placeholder="A short note for whoever sets this up next"
              />
            </div>
            <div className="grid gap-2 sm:w-40">
              <Label htmlFor="template-category-sort">Sort order</Label>
              <Input
                id="template-category-sort"
                type="number"
                value={form.sortOrder}
                onChange={(event) => updateForm({ sortOrder: event.target.value })}
              />
            </div>
            {error && <p className="text-sm text-red-600">{error}</p>}
          </div>

          <div className="grid gap-3 sm:grid-cols-2">
            {languages.map((language) => (
              <CustomerSlugInput
                key={language}
                id={`template-category-customer-slug-${language}`}
                label={`${language.toUpperCase()} category slug`}
                value={form.category_slug[language] ?? ""}
                disabled={busy}
                onChange={(value) =>
                  updateForm({
                    category_slug: { ...form.category_slug, [language]: value },
                  })
                }
              />
            ))}
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setDialog(null)} disabled={busy}>
              Cancel
            </Button>
            <Button type="button" onClick={submitDialog} disabled={busy}>
              {busy && <Loader2 className="h-4 w-4 mr-1 animate-spin" />}
              {dialog?.mode === "create" ? "Create" : "Save"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
